import React, { useCallback, useState } from "react";

import { AccessConfigurationTabContent } from "./ComputeNodeSetupControls";
import type { ComputeNodeNetworkMode } from "./types";

export const SetupSection: React.FC<{
  sshAccess: Array<{ userId: string; linuxUser: string }>;
  isSSHEnabled: boolean;
  usesCertAuthSSHProvider: boolean;
  onCheckSSHAccess: () => Promise<void>;
  onGrantUser: (userId: string, linuxUser: string) => Promise<void>;
  onRevokeUser: (userId: string, linuxUser: string) => Promise<void>;
  networkMode: ComputeNodeNetworkMode;
  onNetworkModeChange: (mode: ComputeNodeNetworkMode) => Promise<void>;
}> = ({
  sshAccess,
  isSSHEnabled,
  usesCertAuthSSHProvider,
  onCheckSSHAccess,
  onGrantUser,
  onRevokeUser,
  networkMode,
  onNetworkModeChange,
}) => {
  const [updatingNetworkMode, setUpdatingNetworkMode] =
    useState<ComputeNodeNetworkMode | null>(null);

  const handleNetworkModeChange = useCallback(
    async (mode: ComputeNodeNetworkMode) => {
      if (updatingNetworkMode || mode === networkMode) {
        return;
      }

      setUpdatingNetworkMode(mode);

      try {
        await onNetworkModeChange(mode);
      } finally {
        setUpdatingNetworkMode(null);
      }
    },
    [networkMode, onNetworkModeChange, updatingNetworkMode],
  );

  return (
    <>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-base font-semibold text-zinc-200">
              Setup this device
            </span>
            <span className="text-sm font-normal text-zinc-500">
              (optional)
            </span>
          </div>
          <div className="mt-1 text-sm leading-[1.6] text-zinc-400">
            Configure how this compute node is exposed on the network and who
            can access it.
          </div>
        </div>
      </div>
      <AccessConfigurationTabContent
        networkMode={networkMode}
        isNetworkModeUpdating={Boolean(updatingNetworkMode)}
        onNetworkModeChange={handleNetworkModeChange}
        sshAccess={sshAccess}
        isSSHEnabled={isSSHEnabled}
        usesCertAuthSSHProvider={usesCertAuthSSHProvider}
        onCheckSSHAccess={onCheckSSHAccess}
        onGrantUser={onGrantUser}
        onRevokeUser={onRevokeUser}
      />
    </>
  );
};
